import React from "react";
import { Grid, Button, FormControlLabel, Typography, Radio } from "material-ui";
import LensIcon from "material-ui-icons/Lens";
import {
    reduxForm,
    FieldArray,
    formValueSelector,
    InjectedFormProps,
    arrayPush,
    arrayRemove,
    Field,
    getFormValues,
} from "redux-form";
import ExtendedField from "components/form/ExtendedField";
import renderCheckBox from "components/form/RenderCheckBox";
import renderTextField from "components/form/RenderTextField";
import { ReduxDispatch, ReduxState } from "ducks/reducers";
import { connect } from "react-redux";
import renderRadio from "components/form/RenderRadio";
import CustomRadioIcon from "components/CustomRadioIcon/CustomRadioIcon";
import CustomRadioIconChecked from "components/CustomRadioIcon/CustomRadioIconChecked";
import renderTextfield from "components/form/RenderTextField";
import CustomCheckboxIcon from "components/CustomCheckbox/CustomCheckboxIcon";
import CustomCheckboxIconChecked from "components/CustomCheckbox/CustomCheckboxIconChecked";
import CalendarIcon from "components/CalendarIcon/CalendarIcon";
import renderCustomSelectField from "components/form/RenderCustomSelectField";
import { KYCDataDto } from "util/DTOs/KYCDtos";
import moment from "moment";
import CustomExtendedField from "components/form/CustomExtendedField";
import validateKYCForm from "./KYCFormValidator";
import { Group, KYCForm, Question } from "types";
import { KYCQuestionType } from "types/enums";
import { Country } from "../../../util/interfaces";
import InfoText from "components/form/QuestionParameters/InfoText";
import KycHeader from "components/form/QuestionParameters/KycHeader";
import { isRequired, mapCountryAnswers } from "util/kycUtils";

interface ForeignCountryWithTaxation {
    countryHasNotIssuedATaxNumber: boolean;
    taxationCountry: string;
    taxationNumber: string;
}

interface OwnProps {
    form: string;
    onSubmit: (data: any) => Promise<void>;
    initialValues?: KYCDataDto;
}

interface KYCFormStateProps {
    politicallyExposed: boolean;
    formState: any;
    kycForm?: KYCForm;
    countries?: Country[];
}
interface KYCFormDispatchProps {
    addItemToFieldArray: (
        formName: string,
        formField: string,
        value: any
    ) => void;
    removeItemFromFieldArray: (
        formName: string,
        formField: string,
        index: number
    ) => void;
}

type KYCFormProps = OwnProps &
    KYCFormStateProps &
    KYCFormDispatchProps &
    InjectedFormProps<KYCDataDto, OwnProps>;

const styles: React.CSSProperties = {
    selectMenu: {
        fontSize: "18px",
        color: "#646363",
    },
    selectMenuLabel: { fontSize: "10px", color: "#646363" },
    selectMenuInterval: {
        fontSize: "18px",
        color: "#646363",
    },
};

const DynamicKYCForm: React.SFC<KYCFormProps> = (props: KYCFormProps) => {
    /* tslint:disable:no-string-literal */
    const renderQuestions = (group: Group) => {
        return group.questions.map((question, index) => {
            return renderQuestionByType(
                question,
                props,
                `${question.name}_${index}`
            );
        });
    };
    /* tslint:disable:no-string-literal */

    const renderGroups = () => {
        const groupCount = props.kycForm && props.kycForm.groups.length;

        return (
            props.kycForm &&
            props.kycForm.groups.map((group, index) => {
                return (
                    <Grid key={`${group.titleFI}${group.id}`} container>
                        <Grid item xs={12}>
                            <Grid container>
                                <h4
                                    id="basicInfoHeader"
                                    style={{
                                        fontWeight: 600,
                                        color: "#646363",
                                        fontSize: "18px",
                                        marginBottom: 0,
                                        padding: "8px",
                                    }}
                                    tabIndex={0}
                                >
                                    {`${index + 1}/${groupCount} ${
                                        group.titleFI
                                    }`}
                                </h4>
                            </Grid>
                            {renderQuestions(group)}
                        </Grid>
                    </Grid>
                );
            })
        );
    };
    return (
        <form onSubmit={props.handleSubmit}>
            <Typography
                id="kycFormStartText"
                className="text-area"
                style={{ marginBottom: 10, fontSize: 14 }}
                tabIndex={0}
            >
                Suomen Hypoteekkiyhdistyksellä (Hypo) ja Suomen Asuntohypopankki
                Oy:llä (AHP) on lain nojalla velvollisuus hankkia asiakkaistaan
                asiakassuhteen perustamisen yhteydessä laissa säädetyt
                tuntemistiedot. Tuntemistietoja on säännöllisesti päivitettävä
                myös asiakassuhteen aikana. Tälle sivulle on koottu asiointinne
                yhteydessä saadut tuntemistiedot, jotka Hypo ja AHP ovat
                velvollisia säilyttämään vähintään laissa säädetyn määräajan.
            </Typography>
            <Typography
                className="text-area"
                style={{ marginBottom: 10, fontSize: 14 }}
                tabIndex={0}
            >
                Pakolliset kysymykset on merkitty
                {
                    <LensIcon
                        aria-label="Pakollinen"
                        aria-hidden={false}
                        className={"hypo-lens-icon lens-icon"}
                    />
                }{" "}
                -merkillä
            </Typography>
            {renderGroups()}
            <Grid container>
                <Grid item xs={12} style={{ marginTop: 20, marginBottom: 20 }}>
                    <h5 style={{ fontWeight: "normal" }} tabIndex={0}>
                        HENKILÖTIETOJEN KÄSITTELY
                    </h5>
                    <Typography
                        className="text-area"
                        style={{ marginBottom: 10, fontSize: 14 }}
                        tabIndex={0}
                    >
                        Tämä sivu sisältää Suomen Hypoteekkiyhdistyksen ja
                        Suomen Asuntohypopankki Oy:n asiakkaiden henkilötietoja.
                        Suomen Hypoteekkiyhdistys ja Suomen Asuntohypopankki Oy
                        käsittelevät rekisterinpitäjinä henkilötietoja
                        Hypo-konsernin henkilörekistereistä julkistettujen
                        tietosuojaselosteiden mukaisella tavalla.
                        Tietosuojaselosteet ovat saatavissa Hypon verkkosivuilla
                        osoitteessa{" "}
                        <a href="https://www.hypo.fi/tietosuoja">
                            www.hypo.fi/tietosuoja
                        </a>
                        .
                    </Typography>
                    <h5 style={{ fontWeight: "normal" }} tabIndex={0}>
                        TUNTEMISTIETOJEN KÄYTTÄMINEN
                    </h5>
                    <Typography
                        className="text-area"
                        style={{ marginBottom: 20, fontSize: 14 }}
                        tabIndex={0}
                    >
                        Asiakkaan tuntemistietoja ja muita henkilötietoja
                        voidaan käyttää rahanpesun ja terrorismin rahoittamisen
                        estämiseen, paljastamiseen ja selvittämiseen sekä
                        rahanpesun ja terrorismin rahoittamisen ja sen rikoksen,
                        jolla rahanpesun tai terrorismin rahoittamisen kohteena
                        oleva omaisuus tai rikoshyöty on saatu, tutkintaan
                        saattamista varten.
                    </Typography>
                    <Button
                        color="primary"
                        fullWidth={true}
                        variant="flat"
                        type="submit"
                        style={{ minHeight: "54px", minWidth: 0 }}
                        className={"kyc-submit-button"}
                    >
                        Tallenna
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

const renderSelect = (
    question: Question,
    options: { label: string; value: string }[],
    key?: string,
    innerElement?: React.ReactNode,
    formState?: any,
    renderAlternativeQuestionTitle: boolean = false
): JSX.Element => {
    return (
        <Grid
            container
            style={{
                marginTop: 10,
                marginBottom: 10,
                marginLeft: 0,
                marginRight: 0,
                padding: 0,
            }}
            key={key}
        >
            {question.parameters.header && (
                <KycHeader
                    text={question.parameters.header}
                    style={{ fontSize: "18px" }}
                />
            )}
            {question.parameters.subHeader && (
                <KycHeader
                    text={question.parameters.subHeader}
                    style={{ fontSize: "16px" }}
                />
            )}
            <Grid item xs={12} style={{ padding: 0 }}>
                {question.parameters.infoText && (
                    <InfoText
                        id={question.name}
                        style={{
                            marginTop: 0,
                            fontSize: "14px",
                            padding: "0px 8px 8px 8px",
                        }}
                        text={question.parameters.infoText}
                    />
                )}
                {question.parameters.secondInfoText && (
                    <InfoText
                        id={question.name}
                        style={{
                            marginTop: 0,
                            fontSize: "14px",
                            padding: "0px 8px 8px 8px",
                        }}
                        text={question.parameters.secondInfoText}
                    />
                )}
            </Grid>
            <Grid item xs={8} md={8}>
                <CustomExtendedField
                    id={question.name}
                    name={question.name}
                    component={renderCustomSelectField}
                    label={
                        renderAlternativeQuestionTitle
                            ? question.parameters.alternativeQuestionTitle
                            : question.titleFI
                    }
                    className={
                        "kyc-select hypo-blue-underline-kyc hyposelect-arrow-kyc"
                    }
                    options={options}
                    renderEmptyCellItem={true}
                    adornment={question.parameters.adornment}
                    required={isRequired(question, formState)}
                />
            </Grid>
            {innerElement}
        </Grid>
    );
};

const renderSelectionListQuestion = (
    question: Question,
    options: { label: string; value: string }[],
    key?: string,
    formState?: any
): JSX.Element => {
    return (
        <Grid
            container
            style={{
                marginTop: 10,
                marginBottom: 10,
                marginLeft: 0,
                marginRight: 0,
                padding: 0,
            }}
            key={key}
        >
            {question.parameters.header && (
                <KycHeader
                    text={question.parameters.header}
                    style={{ fontSize: "18px" }}
                />
            )}
            {question.parameters.infoText && (
                <InfoText
                    id={question.name}
                    style={{
                        fontSize: "14px",
                        padding: "0px 8px 8px 8px",
                        marginTop: 0,
                    }}
                    text={question.parameters.infoText}
                />
            )}
            <Grid item xs={12}>
                <FieldArray
                    name={question.name}
                    component={(fieldArrayProps) =>
                        renderSelectionList(
                            fieldArrayProps,
                            question,
                            options,
                            isRequired(question, formState)
                        )
                    }
                />
            </Grid>
        </Grid>
    );
};

const renderQuestionByType = (
    question: Question,
    props: KYCFormProps,
    key?: string,
    innerElement?: React.ReactNode,
    /** Prevent questions in MultiPart sections from re-rendering entire sections every onChange-event */
    onlyChangeOnBlur: boolean = false,
    isMultipartQuestion: boolean = false,
    renderAlternativeQuestionTitle: boolean = false
): React.ReactNode => {
    const type = question.questionType;
    const formState = props.formState;
    switch (type) {
        case KYCQuestionType.CheckBox:
            return (
                <Grid
                    container
                    item
                    xs={isMultipartQuestion ? 9 : 12}
                    style={{
                        marginTop: 10,
                        marginBottom: 10,
                        marginLeft: 0,
                        marginRight: 0,
                        padding: 0,
                    }}
                    key={key}
                >
                    {question.parameters.infoText && (
                        <InfoText
                            id={question.name}
                            style={{
                                fontSize: "18px",
                                padding: "8px 0px",
                            }}
                            text={question.parameters.infoText}
                        />
                    )}
                    <Grid item xs={12} md={8}>
                        <FormControlLabel
                            classes={{
                                label:
                                    "kyc-labeled-checkbox kyc-labeled-checkbox-label",
                            }}
                            control={
                                <ExtendedField
                                    id={question.name}
                                    component={renderCheckBox}
                                    name={question.name}
                                    type="checkbox"
                                    normalize={(value) => !!value}
                                    className={
                                        "kyc-labeled-checkbox kyc-labeled-checkbox-checkbox"
                                    }
                                    customCheckboxIcon={<CustomCheckboxIcon />}
                                    customCheckboxIconChecked={
                                        <CustomCheckboxIconChecked />
                                    }
                                />
                            }
                            label={
                                <div style={{ marginLeft: 10 }}>
                                    {question.titleFI}
                                    {question.mandatory && (
                                        <span>
                                            <LensIcon
                                                aria-label="Pakollinen"
                                                className={
                                                    "hypo-lens-icon lens-icon"
                                                }
                                            />
                                        </span>
                                    )}
                                </div>
                            }
                        />
                    </Grid>
                </Grid>
            );
        case KYCQuestionType.Select:
            return renderSelect(
                question,
                mapCountryAnswers({
                    filter: "Suomi",
                    answers: question.answers,
                    countries: undefined,
                }),
                key,
                null,
                formState
            );
        case KYCQuestionType.SelectionList:
            return renderSelectionListQuestion(
                question,
                mapCountryAnswers({
                    filter: "Suomi",
                    answers: question.answers,
                    countries: undefined,
                }),
                key,
                formState
            );
        case KYCQuestionType.RadioButton:
            return (
                <Grid
                    container
                    style={{
                        marginLeft: 0,
                        marginRight: 0,
                        padding: 0,
                    }}
                    key={key}
                >
                    {question.parameters.infoText && (
                        <InfoText
                            id={question.name}
                            style={{
                                fontSize: "18px",
                                padding: "8px",
                            }}
                            text={question.parameters.infoText}
                        />
                    )}
                    <Grid item xs={12} md={12}>
                        <Field
                            name={question.name}
                            component={renderRadio}
                            required={true}
                            id={`Radio_${key}`}
                        >
                            {question.answers.map((answer, idx) => {
                                return (
                                    <FormControlLabel
                                        key={`${answer.titleFI}${idx}`}
                                        classes={{
                                            label: "kyc-radio-label",
                                        }}
                                        value={answer.externalId}
                                        control={
                                            <Radio
                                                icon={<CustomRadioIcon />}
                                                checkedIcon={
                                                    <CustomRadioIconChecked />
                                                }
                                                id={`Radio_${key}`}
                                            />
                                        }
                                        label={question.titleFI}
                                    />
                                );
                            })}
                        </Field>
                    </Grid>
                </Grid>
            );
        case KYCQuestionType.NumberInput:
            return (
                <Grid
                    item
                    xs={8}
                    md={8}
                    style={{ padding: "8px" }}
                    className={"kyc-currency"}
                    key={key}
                >
                    <ExtendedField
                        id={question.name}
                        component={renderTextField}
                        name={question.name}
                        type="number"
                        placeholder=""
                        adornment={question.parameters.adornment}
                        label={question.titleFI}
                        onChange={(event: any) => {
                            if (onlyChangeOnBlur) {
                                event.preventDefault();
                            }
                        }}
                        className={"kyc-text-field"}
                    />
                </Grid>
            );
        case KYCQuestionType.MultiPart:
            return (
                <Grid container key={key}>
                    {question.parameters.header && (
                        <KycHeader
                            text={question.parameters.header}
                            style={{ fontSize: "18px" }}
                        />
                    )}
                    <Grid item xs={12}>
                        <FieldArray
                            name={question.name}
                            component={(fieldArrayProps) =>
                                renderMultipartList(
                                    fieldArrayProps,
                                    question,
                                    props
                                )
                            }
                        />
                    </Grid>
                </Grid>
            );
        case KYCQuestionType.Date:
            return (
                <Grid item xs={8} md={8} className={"kyc-datepicker"} key={key}>
                    <ExtendedField
                        id={question.name}
                        component={renderTextField}
                        name={question.name}
                        type="date"
                        placeholder=""
                        adornment={<CalendarIcon />}
                        label={question.titleFI}
                        className={"kyc-text-field"}
                        format={(value, name) => {
                            return moment(value)
                                .format("YYYY-MM-DD")
                                .toString();
                        }}
                    />
                </Grid>
            );
        case KYCQuestionType.TextInput:
            return (
                <Grid
                    item
                    xs={9}
                    md={9}
                    className={"kyc-foreign-taxation-number"}
                    key={key}
                >
                    {renderAlternativeQuestionTitle &&
                        question.parameters.infoText && (
                            <InfoText
                                id={question.name}
                                style={{
                                    marginTop: 0,
                                    fontSize: "14px",
                                    padding: "0px 8px 8px 0px",
                                }}
                                text={question.parameters.infoText}
                            />
                        )}
                    <ExtendedField
                        id={question.name}
                        component={renderTextfield}
                        name={question.name}
                        type="text"
                        label={
                            renderAlternativeQuestionTitle
                                ? question.parameters.alternativeQuestionTitle
                                : question.titleFI
                        }
                        onChange={(event: any) => {
                            if (onlyChangeOnBlur) {
                                event.preventDefault();
                            }
                        }}
                        className={"kyc-text-field"}
                        required={isRequired(question, formState)}
                    />
                </Grid>
            );
        case KYCQuestionType.CountrySelect:
            return (
                props.countries &&
                renderSelect(
                    question,
                    mapCountryAnswers({
                        filter: "Suomi",
                        answers: undefined,
                        countries: props.countries,
                    }),
                    key,
                    innerElement,
                    formState,
                    renderAlternativeQuestionTitle
                )
            );
        case KYCQuestionType.CountrySelectionList:
            return (
                props.countries &&
                renderSelectionListQuestion(
                    question,
                    mapCountryAnswers({
                        filter: "Suomi",
                        answers: undefined,
                        countries: props.countries,
                    }),
                    key
                )
            );
        default:
            return null;
    }
};

const renderMultipartList = (
    fieldArrayProps: any,
    question: Question,
    props: KYCFormProps
) => {
    if (fieldArrayProps.fields.length === 0) {
        fieldArrayProps.fields.push();
    }

    return (
        <ul aria-live="polite" style={{ listStyle: "none", paddingLeft: 0 }}>
            {fieldArrayProps.fields &&
                fieldArrayProps.fields.map((country: any, key: any) => {
                    const additionalQuestions: Question[] = JSON.parse(
                        JSON.stringify(question.additionalQuestions)
                    );
                    if (key !== 0) {
                        return (
                            <li key={key}>
                                <Grid
                                    container
                                    style={{
                                        padding: 0,
                                        margin: 0,
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    {additionalQuestions &&
                                        additionalQuestions.map((q, index) => {
                                            q.name = `${country}.${q.name}`;
                                            return renderQuestionByType(
                                                q,
                                                props,
                                                `${q.name}_sub_${index}`,
                                                index === 0 ? (
                                                    <Grid item xs={4}>
                                                        <Button
                                                            color="primary"
                                                            fullWidth={true}
                                                            variant="flat"
                                                            onClick={() =>
                                                                fieldArrayProps.fields.remove(
                                                                    key
                                                                )
                                                            }
                                                            style={{
                                                                minHeight:
                                                                    "54px",
                                                                minWidth: 0,
                                                            }}
                                                            className={
                                                                "add-country-kyc"
                                                            }
                                                        >
                                                            {
                                                                question
                                                                    .parameters
                                                                    .removeButtonTitle
                                                            }
                                                        </Button>
                                                    </Grid>
                                                ) : null,
                                                true,
                                                true
                                            );
                                        })}
                                </Grid>
                            </li>
                        );
                    }
                    return (
                        <li key={key}>
                            <Grid
                                container
                                style={{
                                    padding: 0,
                                    margin: 0,
                                    justifyContent: "flex-end",
                                }}
                            >
                                {additionalQuestions &&
                                    additionalQuestions.map((q, index) => {
                                        q.name = `${country}.${q.name}`;
                                        return renderQuestionByType(
                                            q,
                                            props,
                                            `add_${index}`,
                                            null,
                                            true,
                                            true,
                                            true
                                        );
                                    })}
                            </Grid>
                        </li>
                    );
                })}
            <li>
                <Grid container>
                    <Grid
                        item
                        xs={12}
                        style={{ marginTop: 20, marginBottom: 20 }}
                    >
                        <Button
                            id={`${question.name}Button`}
                            color="primary"
                            fullWidth={true}
                            variant="flat"
                            onClick={() => {
                                fieldArrayProps.fields.push();
                            }}
                            style={{ minHeight: "54px", minWidth: 0 }}
                            className={"add-country-kyc"}
                        >
                            {question.parameters.addButtonTitle}
                        </Button>
                    </Grid>
                </Grid>
            </li>
        </ul>
    );
};

const renderSelectionList = (
    fieldArrayProps: any,
    question: Question,
    listOptions: { label: string; value: string }[],
    required: boolean
) => {
    if (fieldArrayProps.fields.length === 0) {
        fieldArrayProps.fields.push();
    }
    const options = listOptions;
    return (
        <ul aria-live="polite" style={{ listStyle: "none", paddingLeft: 0 }}>
            {fieldArrayProps.fields &&
                fieldArrayProps.fields.map((fieldArrayProp: any, key: any) => {
                    if (key !== 0) {
                        return (
                            <li key={key}>
                                <Grid container>
                                    <Grid item xs={8}>
                                        <CustomExtendedField
                                            id={`${question.name}_${key}`}
                                            name={`${fieldArrayProp}`}
                                            component={renderCustomSelectField}
                                            label={question.titleFI}
                                            className={
                                                "kyc-select hypo-blue-underline-kyc hyposelect-arrow-kyc"
                                            }
                                            options={options}
                                            renderEmptyCellItem={true}
                                            required={required}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Button
                                            color="primary"
                                            fullWidth={true}
                                            variant="flat"
                                            onClick={() =>
                                                fieldArrayProps.fields.remove(
                                                    key
                                                )
                                            }
                                            style={{
                                                minHeight: "54px",
                                                minWidth: 0,
                                            }}
                                            className={"add-country-kyc"}
                                        >
                                            {
                                                question.parameters
                                                    .removeButtonTitle
                                            }
                                        </Button>
                                    </Grid>
                                </Grid>
                            </li>
                        );
                    }
                    return (
                        <li key={key} style={{ height: "70px" }}>
                            <Grid container>
                                <Grid item xs={8} md={8}>
                                    <CustomExtendedField
                                        id={`${question.name}_${key}`}
                                        name={`${fieldArrayProp}`}
                                        component={renderCustomSelectField}
                                        label={question.titleFI}
                                        className={
                                            "kyc-select hypo-blue-underline-kyc hyposelect-arrow-kyc"
                                        }
                                        options={options}
                                        renderEmptyCellItem={true}
                                        required={required}
                                    />
                                </Grid>
                            </Grid>
                        </li>
                    );
                })}
            <li>
                <Grid container>
                    <Grid
                        item
                        xs={12}
                        style={{ marginTop: 20, marginBottom: 20 }}
                    >
                        <Button
                            id={`${question.name}Button`}
                            color="primary"
                            fullWidth={true}
                            variant="flat"
                            onClick={() => {
                                fieldArrayProps.fields.push();
                            }}
                            style={{ minHeight: "54px", minWidth: 0 }}
                            className={"add-country-kyc"}
                            disabled={fieldArrayProps.fields.length > 3}
                        >
                            {question.parameters.addButtonTitle}
                        </Button>
                    </Grid>
                </Grid>
            </li>
        </ul>
    );
};

const kycForm = reduxForm<KYCDataDto, OwnProps>({
    form: "kycForm",
    enableReinitialize: true,
    validate: validateKYCForm,
})(DynamicKYCForm);

const mapStateToProps = (state: ReduxState): KYCFormStateProps => {
    const selector = formValueSelector("kycform");
    return {
        politicallyExposed: selector(state, "politicallyExposed"),
        formState: getFormValues("kycform")(state),
        kycForm: state.kyc.form,
        countries: state.mortgage.countries,
    };
};

const mapDispatchToProps = (dispatch: ReduxDispatch): KYCFormDispatchProps => {
    return {
        addItemToFieldArray: (
            formName: string,
            formField: string,
            value: any
        ) => dispatch(arrayPush(formName, formField, value)),
        removeItemFromFieldArray: (
            formName: string,
            formField: string,
            index: number
        ) => dispatch(arrayRemove(formName, formField, index)),
    };
};

export default connect<KYCFormStateProps, KYCFormDispatchProps, OwnProps>(
    mapStateToProps,
    mapDispatchToProps
)(kycForm as any);



// WEBPACK FOOTER //
// ./src/views/profile/components/DynamicKYCForm.tsx